import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { emailProblem } from '@fsm/shared';
import { api } from '../lib/api';
import { Button, Field, Input } from '../components/ui';
import { AuthShell } from '../components/AuthShell';

/**
 * Asking for a way back in.
 *
 * The confirmation is carefully worded, and the wording is the feature. It
 * says "if there is an account" rather than "we have sent you an email",
 * because the server deliberately does not tell anyone whether an address has
 * an account — a screen that said "sent!" for real addresses and something
 * else for the rest would hand back exactly the answer the server withheld.
 *
 * The cost is a slightly hedged sentence for somebody who mistyped their own
 * address. The benefit is that this form is useless to anyone working through
 * a list of addresses to find out who banks here.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);

    const bad = emailProblem(email);
    setProblem(bad);
    if (bad) return;

    setBusy(true);
    try {
      await api.auth.forgotPassword(email);
      setSent(true);
    } catch (error) {
      /*
       * Only transport and rate-limit failures reach here — the route answers
       * 200 whether or not the account exists. So this is an honest "that did
       * not go through", not a hint about the address.
       */
      setFailure((error as { message?: string }).message ?? 'Could not send that just now');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <p className="text-[13.5px] leading-relaxed text-ink-2">
          If there is an account for <span className="font-semibold">{email.trim()}</span>, a
          link to choose a new password is on its way. It works for an hour, and only once.
        </p>
        <p className="mt-3 text-[12.5px] leading-relaxed text-ink-4">
          Nothing after a few minutes? Check the spam folder, and make sure the address above
          is the one you sign in with.
        </p>
        <Link
          to="/login"
          className="mt-5 block text-center text-[13px] font-semibold text-accent"
        >
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="Enter the address you sign in with and we will send a link to set a new one."
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" error={problem}>
          <Input
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            onChange={(e) => {
              setEmail(e.target.value);
              setProblem(null);
            }}
          />
        </Field>

        {failure && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{failure}</p>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Send reset link
        </Button>
      </form>

      <p className="mt-5 text-center text-[13px] text-ink-3">
        <Link to="/login" className="font-semibold text-accent">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
